import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

/**
 * A date field.
 *
 * Values move as YYYY-MM-DD strings because that is what the API takes and what the database
 * stores — converting to a Date and back at every boundary is where timezone drift creeps in and
 * a holiday quietly lands a day early.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  clearable = true,
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  clearable?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={ariaLabel ?? placeholder}
          className={cn(
            'w-full justify-start px-3 font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="size-4 opacity-60" />
          <span className="flex-1 text-left">
            {selected ? format(selected, 'd MMM yyyy') : placeholder}
          </span>
          {clearable && value && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear date"
              className="rounded p-0.5 opacity-60 hover:bg-muted hover:opacity-100"
              onClick={(event) => {
                // Stops the popover opening; clearing and picking are different intents.
                event.stopPropagation();
                onChange('');
              }}
            >
              <X className="size-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          {...(selected ? { selected, defaultMonth: selected } : {})}
          onSelect={(date) => {
            onChange(date ? format(date, 'yyyy-MM-dd') : '');
            setOpen(false);
          }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
